import type { FogBugzCase } from "@/hooks/queries/use-fogbugz";
import { cn } from "@/lib/utils";

interface CaseRowProps {
  caseItem: FogBugzCase;
  onNavigate: (id: number) => void;
}

/**
 * Renders a single FogBugz case as a table row with click and keyboard navigation.
 */
export function CaseRow({ caseItem, onNavigate }: CaseRowProps) {
  // Defensive: FogBugz can theoretically return malformed timestamps; show "-"
  // rather than the literal "Invalid Date" string. Mirrors the grouping logic
  // in groupCasesByProjectAndArea which treats unparseable dates as missing.
  const parsedDate = caseItem.dtLastUpdated ? Date.parse(caseItem.dtLastUpdated) : NaN;
  const updatedDate = Number.isFinite(parsedDate)
    ? new Date(parsedDate).toLocaleDateString()
    : "-";

  return (
    <tr
      role="button"
      tabIndex={0}
      aria-label={`Open ticket ${caseItem.ixBug}: ${caseItem.sTitle}`}
      className={cn(
        "border-b cursor-pointer hover:bg-accent/50 transition-colors",
        "focus:outline-none focus-visible:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring"
      )}
      onClick={() => onNavigate(caseItem.ixBug)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onNavigate(caseItem.ixBug);
        }
      }}
    >
      <td className="px-4 py-3 font-mono text-sm">{caseItem.ixBug}</td>
      <td className="px-4 py-3">{caseItem.sTitle}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-primary/10 text-primary">
          {caseItem.sStatus}
        </span>
      </td>
      <td className="px-4 py-3">{caseItem.sPriority}</td>
      <td className="px-4 py-3 text-muted-foreground text-sm">{updatedDate}</td>
    </tr>
  );
}
